import { useWorkspaceStore } from '@editor/stores/workspaceStore';
import { KittlHeaderNames } from '@repo/api-client';
import env from '@repo/env';
import { HeaderNames } from '@repo/transport/fetchRetry';
import { getProfileToken } from '@repo/transport/token';

import { fetchRetry, RequestInitWithRetry, tryFetchRetry } from './fetchRetry';
import { INVITE_ID, LEGACY_INVITE_ID } from './window';

/**
 * Extends the request init with default headers.
 *
 * @param {RequestInitWithRetry} [init] - The request init to extend.
 * @returns {RequestInitWithRetry} The extended request init.
 */
const extendRequestInit = (
  init?: RequestInitWithRetry
): RequestInitWithRetry => {
  const defaultHeaders: HeadersInit = {};
  const params =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();

  /*
   if we aren't sent a workspace ID, assume we are always using on the design workspace
    certain functions will send a specific one, such as image uploading/viewing etc
  */
  const workspaceId = useWorkspaceStore.getState().designWorkspaceId;
  if (workspaceId) {
    defaultHeaders[KittlHeaderNames.WORKSPACE_ID] = workspaceId;
  }

  // we support both legacy and new invite id
  const inviteId = params.get(LEGACY_INVITE_ID) || params.get(INVITE_ID);
  if (inviteId) {
    defaultHeaders[KittlHeaderNames.INVITE_ID] = inviteId;
  }

  const authToken = getProfileToken();
  if (authToken) {
    defaultHeaders[HeaderNames.AUTHORIZATION] = `Bearer ${authToken}`;
  }

  // Temp workaround for CMS on Render preview environment
  const isUsingRenderPreview =
    env.renderDomain && env.apiUrl.includes(env.renderDomain);
  if (isUsingRenderPreview) {
    defaultHeaders[KittlHeaderNames.PREVIEW_DEPLOYMENT] = 'render';
  }

  if (init?.body && init.method && !['GET', 'HEAD'].includes(init.method)) {
    if (init.body instanceof FormData) {
      // Browser sets Content-Type for FormData automatically
    } else if (init.body instanceof URLSearchParams) {
      defaultHeaders[HeaderNames.CONTENT_TYPE] =
        'application/x-www-form-urlencoded';
    } else if (
      init.body instanceof Blob ||
      init.body instanceof File ||
      init.body instanceof ArrayBuffer ||
      ArrayBuffer.isView(init.body)
    ) {
      // Let the browser handle content-type for binary data
    } else if (typeof init.body === 'string') {
      // we assume it's a JSON
      // we don't handle 'text/plain' here as it's unlikely
      defaultHeaders[HeaderNames.CONTENT_TYPE] = 'application/json';
    } else if (typeof init.body === 'object' && init.body !== null) {
      defaultHeaders[HeaderNames.CONTENT_TYPE] = 'application/json';
    }
  }

  const mergedInit: RequestInitWithRetry = {
    ...init,
    headers: {
      ...defaultHeaders,
      ...init?.headers, // User-provided headers take precedence
    },
  };

  return mergedInit;
};

/**
 * Performs an API fetch request with retry functionality, applying global headers.
 *
 * @param {RequestInfo} input - The URL or Request object.
 * @param {RequestInitWithRetry} [init] - Custom settings to apply to the request.
 * @returns {Promise<Response>} A promise that resolves with the Response object.
 */
export function apiFetchRetry(
  input: RequestInfo,
  init?: RequestInitWithRetry
): Promise<Response> {
  return fetchRetry(input, extendRequestInit(init));
}

/**
 * Attempts an API fetch request with retry functionality, applying global headers.
 * This function handles errors and can return either the parsed response or a standardized error object.
 *
 * @template T - The expected type of the response data.
 * @param {RequestInfo} input - The URL or Request object.
 * @param {RequestInitWithRetry} [init] - Custom settings to apply to the request.
 * @param {boolean} [returnRawResponse=false] - If true, returns the raw Response object instead of parsing it.
 * @param {number} [timeToExpire=0] - Time in milliseconds after which the request should expire.
 * @returns {Promise<T | { success: false; error: string; errorObject?: Error }>} A promise that resolves with the response data or an error object.
 */
export async function apiTryFetchRetry<T>(
  input: RequestInfo,
  init?: RequestInitWithRetry,
  returnRawResponse: boolean = false,
  timeToExpire: number = 0
): Promise<T | { success: false; error: string; errorObject?: Error }> {
  return tryFetchRetry<T>(
    input,
    extendRequestInit(init),
    returnRawResponse,
    timeToExpire
  );
}
